import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook to track if component is currently mounted
 * Prevents state updates on unmounted components
 *
 * @returns {React.MutableRefObject<boolean>} isMountedRef - Reference that tracks mount status
 *
 * @example
 * const isMountedRef = useIsMounted();
 *
 * fetch('/api/data')
 *   .then(data => {
 *     if (isMountedRef.current) {
 *       setState(data);
 *     }
 *   });
 */
export const useIsMounted = () => {
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return isMountedRef;
};

/**
 * Safe setTimeout that automatically clears on unmount
 * Prevents memory leaks and errors from timers executing after component unmount
 *
 * @returns {{set: Function, clear: Function}} Object with set and clear methods
 *
 * @example
 * const timeout = useTimeout();
 *
 * timeout.set(() => {
 *   console.log('Executed after delay');
 * }, 1000);
 *
 * // Manual clear if needed (optional - auto-clears on unmount)
 * timeout.clear();
 */
export const useTimeout = () => {
  const timeoutRef = useRef(null);

  const clear = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const set = useCallback((callback, delay) => {
    clear(); // Clear any existing timeout
    timeoutRef.current = setTimeout(callback, delay);
    return timeoutRef.current;
  }, [clear]);

  useEffect(() => {
    return () => clear();
  }, [clear]);

  return { set, clear };
};

/**
 * Safe setInterval that automatically clears on unmount
 * Prevents memory leaks from intervals running after component unmount
 *
 * @returns {{set: Function, clear: Function}} Object with set and clear methods
 *
 * @example
 * const interval = useInterval();
 *
 * interval.set(() => {
 *   console.log('Repeated action');
 * }, 1000);
 *
 * // Manual clear if needed (optional - auto-clears on unmount)
 * interval.clear();
 */
export const useInterval = () => {
  const intervalRef = useRef(null);

  const clear = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const set = useCallback((callback, delay) => {
    clear(); // Clear any existing interval
    intervalRef.current = setInterval(callback, delay);
    return intervalRef.current;
  }, [clear]);

  useEffect(() => {
    return () => clear();
  }, [clear]);

  return { set, clear };
};

/**
 * Multiple timeout manager for tracking several independent timeouts
 * Useful when you need to manage multiple timers with different purposes
 *
 * @returns {{set: Function, clear: Function, clearAll: Function}} Timeout management methods
 *
 * @example
 * const timeouts = useTimeouts();
 *
 * timeouts.set('alert', () => { hideAlert(); }, 3000);
 * timeouts.set('redirect', () => { navigate('/'); }, 5000);
 *
 * // Clear specific timeout
 * timeouts.clear('alert');
 *
 * // Clear all timeouts (also happens automatically on unmount)
 * timeouts.clearAll();
 */
export const useTimeouts = () => {
  const timeoutsRef = useRef({});

  const clear = useCallback((key) => {
    if (timeoutsRef.current[key]) {
      clearTimeout(timeoutsRef.current[key]);
      delete timeoutsRef.current[key];
    }
  }, []);

  const clearAll = useCallback(() => {
    Object.keys(timeoutsRef.current).forEach(key => {
      clearTimeout(timeoutsRef.current[key]);
    });
    timeoutsRef.current = {};
  }, []);

  const set = useCallback((key, callback, delay) => {
    clear(key); // Clear existing timeout with same key
    timeoutsRef.current[key] = setTimeout(() => {
      callback();
      delete timeoutsRef.current[key];
    }, delay);
    return timeoutsRef.current[key];
  }, [clear]);

  useEffect(() => {
    return () => clearAll();
  }, [clearAll]);

  return { set, clear, clearAll };
};

/**
 * Safe async operation handler with mounted check
 * Combines useIsMounted with async/await pattern
 * Returns null if component unmounted, preventing state update attempts
 *
 * @param {Function} asyncFunction - Async function to execute safely
 * @returns {Function} Safe wrapper function that checks mount status
 *
 * @example
 * const fetchData = useSafeAsync(async () => {
 *   const response = await fetch('/api/data');
 *   return response.json();
 * });
 *
 * const handleClick = async () => {
 *   const data = await fetchData();
 *   if (data) {
 *     // Only updates state if component is still mounted
 *     setState(data);
 *   }
 * };
 */
export const useSafeAsync = (asyncFunction) => {
  const isMountedRef = useIsMounted();

  return useCallback(async (...args) => {
    const result = await asyncFunction(...args);
    return isMountedRef.current ? result : null;
  }, [asyncFunction, isMountedRef]);
};
